import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import QuickreplyOutlinedIcon from "@mui/icons-material/QuickreplyOutlined";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import { apiConstants, postGeneric } from "../../common/constants";
import AnalyticsTableCard from "./AnalyticsTableCard";
import AnalyticsHero, { periodLabel } from "./AnalyticsHero";
import ChartCard, { Segmented } from "./ChartCard";
import KpiTile from "./KpiTile";
import { Gauge } from "./MiniVisuals";
import { ACCENTS, COLORS, SERIES, fmtCount, fmtDuration, fmtPct, pct, rise } from "./emsTheme";

const formatMinutes = fmtDuration;
const formatPct = (p) => (p == null ? "No data" : fmtPct(p));

const n = (v) => (v ?? 0).toLocaleString("en-IN");

// The Freshdesk Agent Performance report's tabs, minus Handling Time / Availability (agent
// online time, which Escalation Management doesn't track).
const VIEWS = [
    {
        key: "activity", label: "Activity",
        columns: (drill) => [
            { key: "assigned", label: "Tickets assigned", text: (r) => n(r.assigned), onClick: drill && ((r) => drill(r)) },
            { key: "resolved", label: "Resolved / closed", text: (r) => n(r.resolved), onClick: drill && ((r) => drill(r, { closedOnly: true })) },
            { key: "reopened", label: "Reopened", text: (r) => n(r.reopened), onClick: drill && ((r) => drill(r, { reopenedOnly: true })) },
            { key: "reassignedAway", label: "Reassigned to someone else", text: (r) => n(r.reassignedAway) },
            { key: "privateNotes", label: "Internal notes", text: (r) => n(r.privateNotes) },
            { key: "responses", label: "Replies", text: (r) => n(r.responses) },
        ],
    },
    {
        key: "time", label: "Time",
        columns: () => [
            { key: "avgFirstResponseMinutes", label: "First response (average)", text: (r) => formatMinutes(r.avgFirstResponseMinutes) },
            { key: "avgResolutionMinutes", label: "Resolution (average)", text: (r) => formatMinutes(r.avgResolutionMinutes) },
        ],
    },
    {
        key: "sla", label: "SLA %",
        columns: () => [
            { key: "resolutionSlaPct", label: "Resolution SLA", text: (r) => formatPct(r.resolutionSlaPct) },
            { key: "firstResponseSlaPct", label: "First response SLA", text: (r) => formatPct(r.firstResponseSlaPct) },
            { key: "resolvedFirstTimePct", label: "Resolved first time", text: (r) => formatPct(r.resolvedFirstTimePct) },
        ],
    },
    {
        key: "violations", label: "Violations",
        columns: () => [
            { key: "firstResponseViolations", label: "First response SLA missed", text: (r) => n(r.firstResponseViolations) },
            { key: "resolutionViolations", label: "Resolution SLA missed", text: (r) => n(r.resolutionViolations) },
        ],
    },
];

const VIEW_INFO = {
    activity: "Per assignee (the ticket's current assignee) for tickets created in the range. Notes and replies count remarks each person wrote; system notes are excluded.",
    time: "First response: first external reply by Prozo staff minus created time. Resolution: resolved time minus created time. Both are averages per ticket.",
    sla: "Resolution SLA: resolved on or before due, out of tickets resolved or already past due. First response SLA: same for the first reply. Resolved first time: resolved and never reopened.",
    violations: "Tickets that missed the first response or resolution due time, resolved late or still open past due.",
};

/** An insight title for the agent table, from the data. */
const agentTitle = (view, rows) => {
    const named = (rows || []).filter((r) => r.key !== "unassigned");
    const unassigned = (rows || []).find((r) => r.key === "unassigned");
    if (!rows || !rows.length) return "Assignee performance";
    if (view === "activity") {
        const top = [...named].sort((a, b) => (b.assigned || 0) - (a.assigned || 0))[0];
        if (unassigned && unassigned.assigned) return `${n(unassigned.assigned)} tickets have no assignee${top && top.assigned ? `; ${top.label} holds the most (${n(top.assigned)})` : ""}`;
        return top ? `${top.label} holds the most tickets (${n(top.assigned)})` : "Activity by assignee";
    }
    if (view === "time") {
        const slow = [...named].filter((r) => r.avgResolutionMinutes != null).sort((a, b) => b.avgResolutionMinutes - a.avgResolutionMinutes)[0];
        return slow ? `${slow.label} has the slowest average resolution, ${formatMinutes(slow.avgResolutionMinutes)}` : "Response and resolution times by assignee";
    }
    if (view === "sla") {
        const low = [...named].filter((r) => r.resolutionSlaPct != null).sort((a, b) => a.resolutionSlaPct - b.resolutionSlaPct)[0];
        return low ? `${low.label} has the lowest resolution SLA, ${formatPct(low.resolutionSlaPct)}` : "SLA by assignee";
    }
    const most = [...(rows || [])].sort((a, b) => (b.resolutionViolations || 0) - (a.resolutionViolations || 0))[0];
    return most && most.resolutionViolations ? `${most.label} has the most resolution SLA misses (${n(most.resolutionViolations)})` : "No resolution SLA misses";
};

// groupBy value -> the ticket-list filter a grouped row drills into.
const GROUP_BY = [
    { key: "severity", label: "Severity", filterKey: "severity" },
    { key: "category", label: "Category", filterKey: "category" },
    { key: "merchant", label: "Merchant", filterKey: "merchantId" },
    { key: "status", label: "Status", filterKey: "status" },
];

const agentPatch = (row) => (row.key === "unassigned" ? { unassignedOnly: true } : { assignedToUserId: row.key });


// ── Small pieces ────────────────────────────────────────────────────────────────────────────

const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
const colourFor = (key) => {
    let h = 0;
    for (const ch of String(key || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return SERIES[h % SERIES.length];
};

/** Initials in a coloured circle; "Unassigned" gets a dashed grey ring. */
export const AgentAvatar = ({ row, size = 28 }) => {
    const unassigned = row.key === "unassigned";
    return (
        <Box aria-hidden sx={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center",
            fontSize: size <= 24 ? 11 : 12, fontWeight: 700, color: unassigned ? COLORS.ink3 : "#fff",
            bgcolor: unassigned ? "transparent" : colourFor(row.key), border: unassigned ? `1.5px dashed ${COLORS.context}` : "none" }}>
            {unassigned ? "–" : initials(row.label)}
        </Box>
    );
};

/** Team-wide SLA from the per-agent rows: tickets on time out of tickets with a verdict. */
const teamRate = (rows, base, missed) => {
    const b = rows.reduce((a, r) => a + (r[base] || 0), 0);
    const m = rows.reduce((a, r) => a + (r[missed] || 0), 0);
    return { base: b, missed: m, pct: b ? ((b - m) * 100) / b : null };
};

/** A thin two-or-more part bar with its legend, for the headline tiles. */
const ShareBar = ({ parts }) => {
    const total = parts.reduce((a, p) => a + (p.value || 0), 0);
    return (
        <Box sx={{ display: "grid", gap: 1, pb: 0.5 }}>
            <Box sx={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", gap: "2px", bgcolor: "#F3F1F7" }} aria-hidden>
                {parts.filter((p) => p.value > 0).map((p) => <Box key={p.label} sx={{ flexGrow: p.value, flexBasis: 0, minWidth: 4, bgcolor: p.color }} />)}
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {parts.map((p) => (
                    <Typography key={p.label} sx={{ fontSize: 12, color: COLORS.ink3, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: p.color }} />
                        {p.label} <b style={{ color: COLORS.ink }}>{fmtCount(p.value)}</b>{total ? ` · ${Math.round((p.value * 100) / total)}%` : ""}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

const slaTone = (p) => (p == null ? COLORS.context : p >= 95 ? ACCENTS.green.solid : p >= 80 ? ACCENTS.orange.solid : ACCENTS.red.solid);

const Legend = ({ items }) => (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {items.map((i) => (
            <Box key={i.label} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: i.color }} />
                <Typography sx={{ fontSize: 12, color: COLORS.ink2 }}>{i.label}</Typography>
            </Box>
        ))}
    </Box>
);

const workloadTitle = (named) => {
    const openOf = (r) => Math.max(0, (r.assigned || 0) - (r.resolved || 0));
    const top = [...named].sort((a, b) => openOf(b) - openOf(a))[0];
    return top && openOf(top) ? `${top.label} has the most still open (${fmtCount(openOf(top))} of ${fmtCount(top.assigned)})` : "Tickets per assignee";
};

/** Per agent: resolved/closed and still-open parts of their tickets on one bar. */
const WorkloadCard = ({ rows, loading, title, onAgent }) => {
    // Unassigned is usually far bigger than any one agent, so it gets its own line and the bars
    // are scaled to the agents only.
    const list = [...rows].filter((r) => r.key !== "unassigned").sort((a, b) => (b.assigned || 0) - (a.assigned || 0));
    const none = rows.find((r) => r.key === "unassigned");
    const noneOpen = none ? Math.max(0, (none.assigned || 0) - (none.resolved || 0)) : 0;
    const max = Math.max(1, ...list.map((r) => r.assigned || 0));
    return (
        <ChartCard loading={loading} eyebrow="Workload" title={title} empty={!loading && !list.length && !none} height={300}
            info="Tickets created in the range, by current assignee: resolved or closed vs still open or pending. Click a name or a part of a bar to open those tickets."
            fileName="ems-agent-workload"
            table={{ columns: [{ key: "label", label: "Assignee" }, { key: "assigned", label: "Assigned", align: "right", text: (r) => fmtCount(r.assigned) },
                { key: "resolved", label: "Resolved / closed", align: "right", text: (r) => fmtCount(r.resolved) },
                { key: "open", label: "Still open", align: "right", text: (r) => fmtCount(Math.max(0, (r.assigned || 0) - (r.resolved || 0))) }], rows: none ? [...list, none] : list }}>
            <Box sx={{ display: "grid", gap: 1.5 }}>
                {none && none.assigned > 0 && (
                    <Box component="button" type="button" onClick={() => onAgent(none, noneOpen ? { unresolvedOnly: true } : undefined)}
                        aria-label={`${none.assigned} tickets have no assignee, ${noneOpen} still open. Open these tickets`}
                        sx={{ all: "unset", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, borderRadius: "10px", cursor: "pointer",
                            bgcolor: ACCENTS.orange.soft, border: `1px dashed ${ACCENTS.orange.solid}66`, "&:hover": { borderStyle: "solid" }, "&:focus-visible": { outline: `2px solid ${ACCENTS.orange.solid}` } }}>
                        <AgentAvatar row={none} />
                        <Typography sx={{ fontSize: 13, color: COLORS.ink, flex: 1 }}>
                            <b>{fmtCount(none.assigned)}</b> tickets have no assignee{noneOpen ? <>, <b>{fmtCount(noneOpen)}</b> still open</> : ""}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: ACCENTS.orange.solid }}>Open them</Typography>
                    </Box>
                )}
                <Legend items={[{ label: "Resolved / closed", color: ACCENTS.green.solid }, { label: "Still open", color: COLORS.purple }]} />
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1, maxHeight: 360, overflow: "auto", pr: 0.5 }}>
                    {list.map((r) => {
                        const open = Math.max(0, (r.assigned || 0) - (r.resolved || 0));
                        const w = ((r.assigned || 0) * 100) / max;
                        return (
                            <Box component="li" key={r.key} sx={{ display: "grid", gridTemplateColumns: { xs: "28px minmax(0, 1fr) 56px", sm: "28px minmax(90px, 160px) minmax(0, 1fr) 64px" }, gap: 1.25, alignItems: "center" }}>
                                <AgentAvatar row={r} />
                                <Box component="button" type="button" onClick={() => onAgent(r)} title={r.label} aria-label={`${r.label}: ${r.assigned} tickets. Open these tickets`}
                                    sx={{ all: "unset", cursor: "pointer", fontSize: 13, fontWeight: 600, color: COLORS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        "&:hover": { color: COLORS.purple, textDecoration: "underline" }, "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
                                    {r.label}
                                </Box>
                                <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, height: 14, borderRadius: "7px", bgcolor: "#F3F1F7", overflow: "hidden" }}>
                                    <Box sx={{ width: `${w}%`, height: "100%", display: "flex", gap: "2px", transition: "width 400ms" }}>
                                        {r.resolved > 0 && <Box component="button" type="button" title={`${fmtCount(r.resolved)} resolved / closed`} aria-label={`${r.label}: ${r.resolved} resolved or closed. Open these tickets`}
                                            onClick={() => onAgent(r, { closedOnly: true })} sx={{ all: "unset", cursor: "pointer", flexGrow: r.resolved, flexBasis: 0, bgcolor: ACCENTS.green.solid, "&:hover": { opacity: 0.8 } }} />}
                                        {open > 0 && <Box component="button" type="button" title={`${fmtCount(open)} still open`} aria-label={`${r.label}: ${open} still open. Open these tickets`}
                                            onClick={() => onAgent(r, { unresolvedOnly: true })} sx={{ all: "unset", cursor: "pointer", flexGrow: open, flexBasis: 0, bgcolor: COLORS.purple, "&:hover": { opacity: 0.8 } }} />}
                                    </Box>
                                </Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, textAlign: "right", fontVariantNumeric: "tabular-nums", gridRow: { xs: 1, sm: "auto" }, gridColumn: { xs: 3, sm: "auto" } }}>
                                    {fmtCount(r.assigned)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </ChartCard>
    );
};

const SLA_KINDS = [
    { value: "resolution", label: "Resolution", pct: "resolutionSlaPct", base: "resolutionSlaBase", missed: "resolutionViolations" },
    { value: "firstResponse", label: "First response", pct: "firstResponseSlaPct", base: "firstResponseSlaBase", missed: "firstResponseViolations" },
];

/** SLA % per agent, worst first, coloured by band (95%+ green, 80%+ orange, below that red). */
const SlaCard = ({ rows, loading, onAgent }) => {
    const [kind, setKind] = useState("resolution");
    const k = SLA_KINDS.find((x) => x.value === kind);
    const list = rows.filter((r) => r[k.pct] != null && r[k.base]).sort((a, b) => a[k.pct] - b[k.pct]);
    const worst = list[0];
    return (
        <ChartCard loading={loading} eyebrow={`${k.label} SLA by assignee`} empty={!loading && !list.length} emptyHint="No tickets with a due time yet." height={300}
            title={worst ? `${worst.label} is lowest at ${fmtPct(worst[k.pct], 0)}` : `${k.label} SLA by assignee`}
            info="On time out of tickets that are resolved (or replied to) or already past due. Green is 95% or more, orange 80% or more, red below 80%. Click a name to open that assignee's tickets."
            fileName={`ems-agent-${kind}-sla`}
            actions={<Segmented label="SLA" value={kind} onChange={setKind} options={SLA_KINDS.map((x) => ({ value: x.value, label: x.label }))} />}
            table={{ columns: [{ key: "label", label: "Assignee" }, { key: "p", label: "SLA %", align: "right", text: (r) => fmtPct(r[k.pct]) },
                { key: "m", label: "Missed", align: "right", text: (r) => fmtCount(r[k.missed]) }, { key: "b", label: "Out of", align: "right", text: (r) => fmtCount(r[k.base]) }], rows: list }}>
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.25, maxHeight: 390, overflow: "auto", pr: 0.5 }}>
                {list.map((r) => (
                    <Box component="li" key={r.key} sx={{ display: "grid", gap: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <AgentAvatar row={r} size={22} />
                            <Box component="button" type="button" onClick={() => onAgent(r)} title={r.label}
                                sx={{ all: "unset", cursor: "pointer", flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: COLORS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    "&:hover": { color: COLORS.purple, textDecoration: "underline" }, "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
                                {r.label}
                            </Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: slaTone(r[k.pct]), fontVariantNumeric: "tabular-nums" }}>{fmtPct(r[k.pct], 0)}</Typography>
                            <Typography sx={{ fontSize: 12, color: COLORS.ink3, minWidth: 64, textAlign: "right" }}>{fmtCount(r[k.missed])} of {fmtCount(r[k.base])} late</Typography>
                        </Box>
                        <Box sx={{ height: 8, borderRadius: 4, bgcolor: "#F3F1F7", overflow: "hidden" }} aria-hidden>
                            <Box sx={{ width: `${r[k.pct]}%`, height: "100%", borderRadius: 4, bgcolor: slaTone(r[k.pct]), transition: "width 400ms" }} />
                        </Box>
                    </Box>
                ))}
            </Box>
        </ChartCard>
    );
};

/** One call feeds both Agent performance and Ticket lifecycle; only made while either is open. */
export const useAgentStats = (filters, groupBy, enabled) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const res = await postGeneric(`${apiConstants.ESCALATION_AGENT_STATS_V2}?groupBy=${groupBy}`, filters);
                if (!cancelled) setData(res?.data || null);
            } catch (e) {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [filters, groupBy, enabled]);

    return { data, loading };
};

/**
 * Agent performance — per agent (the ticket's current assignee) for the tickets matching the
 * page filters: a team summary, workload and SLA by agent, then the full tables (activity,
 * response/resolution times, SLA % and SLA misses, and the same split by severity / category /
 * merchant / status). Notes and replies count the remarks each person wrote. Calendar time.
 */
const AgentPerformanceSection = ({ data, loading, groupBy, setGroupBy, onDrilldown, filters }) => {
    const [view, setView] = useState("activity");

    const active = VIEWS.find((v) => v.key === view);
    const group = GROUP_BY.find((g) => g.key === groupBy);
    const drillAgent = (row, extra) => onDrilldown({ ...agentPatch(row), ...(extra || {}) });
    const drillGrouped = (row, extra) => onDrilldown({ ...agentPatch(row), [group.filterKey]: row.groupKey, ...(extra || {}) });

    const agents = data?.agents || [];
    const named = agents.filter((r) => r.key !== "unassigned");
    const unassignedRow = agents.find((r) => r.key === "unassigned");
    const owned = named.reduce((a, r) => a + (r.assigned || 0), 0);
    const unassigned = unassignedRow?.assigned || 0;
    const res = teamRate(agents, "resolutionSlaBase", "resolutionViolations");
    const fr = teamRate(agents, "firstResponseSlaBase", "firstResponseViolations");
    const first = !data && loading;

    const agentCol = { key: "label", label: "Assignee", align: "left", text: (r) => r.label,
        render: (r) => <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}><AgentAvatar row={r} size={22} />{r.label}</Box> };
    const groupCol = { key: "groupLabel", label: group.label, align: "left", text: (r) => r.groupLabel || "-" };

    return (
        <Box sx={{ display: "grid", gap: 2.5, opacity: loading && data ? 0.7 : 1, transition: "opacity 0.15s" }}>
            <AnalyticsHero
                pill={`${periodLabel(filters)} · by the ticket's current assignee`}
                story={first ? "Working out the numbers…" : agents.length ? agentTitle("activity", agents) : "No tickets for these filters."}
                footer={<Typography sx={{ fontSize: 13, opacity: 0.85 }}>Click a name or a bar to open that assignee's tickets. Tickets created in the selected range, calendar time.</Typography>}
                tiles={(
                    <>
                        <KpiTile loading={first} label="Tickets with an owner" value={owned} format={fmtCount} accent="purple" icon={GroupsOutlinedIcon}
                            secondary={owned + unassigned ? `${fmtPct(pct(owned, owned + unassigned), 0)} of ${fmtCount(owned + unassigned)}` : null}
                            visual={owned + unassigned ? <ShareBar parts={[{ label: "With an owner", value: owned, color: COLORS.purple }, { label: "No assignee", value: unassigned, color: ACCENTS.orange.solid }]} /> : null}
                            caption={owned ? "Click to open the tickets with an owner" : "No ticket has an owner yet"}
                            info="Tickets created in the range that have an assignee, out of all tickets created in the range."
                            onClick={owned ? () => onDrilldown({ assignedOnly: true }) : undefined} sx={rise(1)} />
                        <KpiTile loading={first} label="Resolution SLA" value={res.pct != null ? res.pct : "No due times yet"} format={(v) => fmtPct(v)} accent="green" icon={VerifiedOutlinedIcon}
                            secondary={res.base ? `of ${fmtCount(res.base)}` : null}
                            visual={res.pct != null ? <Box sx={{ pb: 1.5 }}><Gauge value={res.pct} color={ACCENTS.green.solid} showValue={false} label={`${fmtPct(res.pct)} resolved on time`} /></Box> : null}
                            caption="Resolved on or before the due time"
                            info="Team-wide: tickets resolved on time out of tickets resolved or already past their resolution due time." sx={rise(2)} />
                        <KpiTile loading={first} label="First response SLA" value={fr.pct != null ? fr.pct : "No due times yet"} format={(v) => fmtPct(v)} accent="blue" icon={QuickreplyOutlinedIcon}
                            secondary={fr.base ? `of ${fmtCount(fr.base)}` : null}
                            visual={fr.pct != null ? <Box sx={{ pb: 1.5 }}><Gauge value={fr.pct} color={ACCENTS.blue.solid} showValue={false} label={`${fmtPct(fr.pct)} replied on time`} /></Box> : null}
                            caption="First reply on or before the due time"
                            info="Team-wide: tickets whose first external reply came on time, out of tickets replied to or already past their first-response due time." sx={rise(3)} />
                        <KpiTile loading={first} label="SLA misses" value={res.missed} format={fmtCount} accent="red" icon={AlarmOnIcon}
                            secondary={fr.missed ? `+ ${fmtCount(fr.missed)} first reply` : null}
                            visual={res.missed + fr.missed ? <ShareBar parts={[{ label: "Resolution", value: res.missed, color: ACCENTS.red.solid }, { label: "First reply", value: fr.missed, color: ACCENTS.orange.solid }]} /> : null}
                            caption={res.missed ? "Click to open the tickets that missed their resolution due time" : "No resolution due time missed"}
                            info="Tickets that missed their resolution due time (resolved late or still open past due), plus first-response misses."
                            onClick={res.missed ? () => onDrilldown({ resolutionSlaMissedOnly: true }) : undefined} sx={rise(4)} />
                    </>
                )}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 7fr) minmax(0, 5fr)" }, gap: 2, alignItems: "start", ...rise(5) }}>
                <WorkloadCard rows={agents} loading={first} title={workloadTitle(named)} onAgent={drillAgent} />
                <SlaCard rows={agents} loading={first} onAgent={drillAgent} />
            </Box>

            <Box component="section" sx={{ display: "grid", gap: 2, mt: 1, ...rise(6) }}>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ flex: "1 1 260px" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: COLORS.purple }}>Details</Typography>
                        <Typography component="h2" sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: COLORS.ink, letterSpacing: "-.01em" }}>Every assignee, every number</Typography>
                        <Typography sx={{ fontSize: 14, color: COLORS.ink3 }}>The same reports Freshdesk had, by assignee. Click a number to see those tickets.</Typography>
                    </Box>
                    <Segmented label="Report" value={view} onChange={setView} options={VIEWS.map((v) => ({ value: v.key, label: v.label }))} />
                </Box>

                <AnalyticsTableCard
                    icon={<SupportAgentOutlinedIcon />}
                    eyebrow={`${active.label} by assignee`}
                    title={agentTitle(view, data?.agents)}
                    sub={view === "activity" ? "Click a number to see those tickets" : `${active.label} by assignee`}
                    info={VIEW_INFO[view]}
                    columns={[agentCol, ...active.columns(drillAgent)]}
                    rows={data?.agents}
                    fileName={`agent-${view}`}
                />

                <AnalyticsTableCard
                    icon={<TableChartOutlinedIcon />}
                    eyebrow={`By assignee and ${group.label.toLowerCase()}`}
                    title={`${active.label} by assignee and ${group.label.toLowerCase()}`}
                    columns={[agentCol, groupCol, ...active.columns(drillGrouped)]}
                    rows={(data?.grouped || []).map((r) => ({ ...r, rowKey: `${r.key}|${r.groupKey}` }))}
                    fileName={`agent-${view}-by-${groupBy}`}
                    headerExtra={<Segmented label="Group by" value={groupBy} onChange={setGroupBy} options={GROUP_BY.map((g) => ({ value: g.key, label: g.label }))} />}
                    maxHeight={460}
                />
            </Box>
        </Box>
    );
};

export default AgentPerformanceSection;
